import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ItemCategoryGroup = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('item_category_groups');
  const [viewItem, setViewItem] = useState(null);
  const [itemCategoryGroups, setItemCategoryGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadItemCategoryGroups();
  }, []);

  const loadItemCategoryGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/item-category-groups`);
      setItemCategoryGroups(response.data || []);
    } catch (error) {
      console.error('Error loading item category groups:', error);
      toast.error('Failed to load item category groups');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (group = null) => {
    setEditingGroup(group);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      item_category_group_id: (formData.get('item_category_group_id') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingGroup) {
        await axios.put(`${BACKEND_URL}/api/item-category-groups/${editingGroup.item_category_group_id}`, {
          description: data.description
        });
        toast.success('Item Category Group updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/item-category-groups`, data);
        toast.success('Item Category Group created successfully');
      }
      setIsDialogOpen(false);
      loadItemCategoryGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item category group');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Item Category Groups</h1>
            <p className="text-sm text-gray-500 mt-1">Manage item category group configurations</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Item Category Groups</h2>
            {writable && (<Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Item Category Group
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : itemCategoryGroups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No item category groups found. Create one to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Group ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {itemCategoryGroups.map((group) => (
                    <tr key={group.item_category_group_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{group.item_category_group_id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{group.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <RecordActions doctype="item_category_groups" onView={() => setViewItem(group)} onEdit={() => handleOpenDialog(group)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingGroup ? 'Edit Item Category Group' : 'Create Item Category Group'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="item_category_group_id">Item Category Group ID * (4 characters)</Label>
                <Input
                  id="item_category_group_id"
                  name="item_category_group_id"
                  defaultValue={editingGroup?.item_category_group_id || ''}
                  disabled={editingGroup !== null}
                  required
                  maxLength={4}
                  minLength={4}
                  pattern="[A-Za-z0-9]{4}"
                  title="Must be exactly 4 alphanumeric characters"
                  className="mt-1 uppercase"
                  placeholder="e.g., NORM, LEIS"
                  onInput={(e) => {
                    e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4);
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Must be exactly 4 alphanumeric characters</p>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Input
                  id="description"
                  name="description"
                  defaultValue={editingGroup?.description || ''}
                  required
                  maxLength={100}
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingGroup ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default ItemCategoryGroup;
